import axios from 'axios';
import { MatchTerm, titleHits } from './watchMatch';

export interface GeneralNewsItem {
  id: number;
  title: string;
  url: string;
  source: string;
  published_at: string;
  fetched_at?: string;
  // Local thumbnail path (/news-images/<id>.jpg) for editorial items; null when
  // an editorial image was unavailable; absent for forum/social items.
  image?: string | null;
}

export type NewsCategory = 'all' | 'news' | 'social' | 'video';

// Forum sources are user-generated discussion ("social"), YouTube items are
// "video", and everything else is edited articles ("news"). The data has no
// explicit type field, so both are derived here.
const SOCIAL_SOURCES = new Set(['WatchUSeek Watch Forums', 'TimeZone']);

/**
 * Video is matched on the URL host, not a source-name list.
 *
 * The YouTube feeds report channel *titles*, which drift -- Bark and Jack
 * already arrives as "Adrian Barker" -- so a name list would silently stop
 * matching after a rename. The host does not drift. Deriving it also means
 * every item already in general-news.json is categorised correctly with no
 * migration: nothing about the stored data changes.
 */
const VIDEO_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
]);

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return '';
  }
};

export const categoryOf = (item: GeneralNewsItem): Exclude<NewsCategory, 'all'> => {
  if (VIDEO_HOSTS.has(hostOf(item.url))) return 'video';
  return SOCIAL_SOURCES.has(item.source) ? 'social' : 'news';
};

// The news list ships as a static JSON file baked into the build
// (client/public/general-news.json -> served at /general-news.json). We fetch
// it once and do the day-window filtering, sorting, and paging in the browser
// -- the same logic the Express server used to do -- so no backend is needed.
let cache: Promise<GeneralNewsItem[]> | null = null;

function loadAll(): Promise<GeneralNewsItem[]> {
  if (!cache) {
    // Cache-bust: the list updates hourly but is served with no Cache-Control
    // header, so browsers heuristically cache it and can show a stale list. A
    // unique query per load forces a fresh copy. (Fetched once per session via
    // the `cache` promise, so this is one request per page load.)
    cache = axios
      .get<GeneralNewsItem[]>(`/general-news.json?t=${Date.now()}`)
      .then(r => r.data)
      .catch(err => {
        cache = null; // allow a retry on failure
        throw err;
      });
  }
  return cache;
}

const effectiveDate = (item: GeneralNewsItem) =>
  new Date(item.published_at || item.fetched_at || 0).getTime();

export const api = {
  getGeneralNews: async (opts?: {
    days?: number;
    limit?: number;
    offset?: number;
    category?: NewsCategory;
    query?: string;
    /**
     * When present, keep only headlines mentioning one of the user's watches.
     * Matching happens here rather than in the pipeline because the whole news
     * list is already in memory -- see watchMatch.ts.
     */
    matchTerms?: MatchTerm[];
  }) => {
    const days = opts?.days ?? null;
    const limit = opts?.limit ?? null;
    const offset = opts?.offset ?? 0;
    const category = opts?.category ?? 'all';
    const query = (opts?.query ?? '').trim().toLowerCase();
    const matchTerms = opts?.matchTerms;

    let items = [...(await loadAll())];

    if (category !== 'all') {
      items = items.filter(item => categoryOf(item) === category);
    }

    if (matchTerms) {
      items = items.filter(item => titleHits(item.title, matchTerms));
    }

    if (query) {
      // Every whitespace-separated term must appear in the title or source.
      const terms = query.split(/\s+/);
      items = items.filter(item => {
        const hay = `${item.title} ${item.source}`.toLowerCase();
        return terms.every(t => hay.includes(t));
      });
    }

    if (days != null && days > 0) {
      const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
      items = items.filter(item => effectiveDate(item) >= cutoff);
    }

    items.sort((a, b) => effectiveDate(b) - effectiveDate(a));

    if (offset > 0) items = items.slice(offset);
    if (limit != null && limit > 0) items = items.slice(0, limit);

    return items;
  },
};
